/**
 * Parent portal API hooks — Phase 8.
 * All four portal endpoints: students, sessions, invoices, balance.
 */

import { useQuery } from '@tanstack/react-query';
import { apiCall } from '@core/api/client';

// ---------------------------------------------------------------------------
// Types (matching backend schemas)
// ---------------------------------------------------------------------------

export interface PortalStudent {
  id: string;
  first_name: string;
  last_name: string;
  status: string;
  date_of_birth?: string;
  notes?: string;
}

export interface PortalSession {
  id: string;
  class_name: string;
  subject_id?: string;
  teacher_id?: string;
  starts_at: string;
  ends_at: string;
  status: string;
}

export interface PortalInvoice {
  id: string;
  number?: string;
  issue_date: string;
  due_date: string;
  total: number;
  amount_due: number;
  status: string;
  currency: string;
}

export interface PortalStudentBalance {
  student_id: string;
  first_name: string;
  last_name: string;
  balance: number;
  currency: string;
}

export interface PortalBalanceResponse {
  students: PortalStudentBalance[];
}

/** 11E.8/D5 — Phase 10B instructor report, read-only guardian view. */
export interface PortalGrade {
  id: string;
  student_id: string;
  student_name?: string;
  instructor_name?: string;
  period_start: string;
  period_end: string;
  submitted_at?: string;
  pdf_ready: boolean;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function usePortalStudents() {
  return useQuery({
    queryKey: ['portal', 'students'],
    queryFn:  () => apiCall<{ items: PortalStudent[] }>('GET', '/v1/portal/me/students'),
    staleTime: 60_000,
  });
}

export function usePortalSessions(from?: string, to?: string) {
  return useQuery({
    queryKey: ['portal', 'sessions', from, to],
    queryFn:  () =>
      apiCall<{ items: PortalSession[] }>('GET', '/v1/portal/me/sessions', undefined, {
        query: { from, to },
      }),
    staleTime: 30_000,
  });
}

export function usePortalInvoices() {
  return useQuery({
    queryKey: ['portal', 'invoices'],
    queryFn:  () => apiCall<{ items: PortalInvoice[] }>('GET', '/v1/portal/me/invoices'),
    staleTime: 60_000,
  });
}

export function usePortalBalance() {
  return useQuery({
    queryKey: ['portal', 'balance'],
    queryFn:  () => apiCall<PortalBalanceResponse>('GET', '/v1/portal/me/balance'),
    staleTime: 60_000,
  });
}

export function usePortalGrades() {
  return useQuery({
    queryKey: ['portal', 'grades'],
    queryFn:  () => apiCall<{ items: PortalGrade[] }>('GET', '/v1/portal/me/grades'),
    staleTime: 60_000,
  });
}

export async function getPortalGradePdfUrl(
  reportId: string,
): Promise<{ download_url: string; expires_at: string }> {
  return apiCall('GET', `/v1/portal/me/grades/${reportId}/pdf`);
}
